import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { map, range } from 'ramda';
import { difference } from 'lodash';

import { Paper, Grid, Typography, LinearProgress } from '@material-ui/core';
import { red, green } from '@material-ui/core/colors';
import Onboarding from '../../../components/Onboarding/components';
import Loader from '../../../components/Loader';
import PasswordUpdate from '../../../components/PasswordUpdate';
import PasswordUpdateSuccess from '../../../components/PasswordUpdateSuccess';
import PasswordUsersUpdate from '../../../components/PasswordUsersUpdate';

import { Chart, ArgumentAxis, ValueAxis, SplineSeries, Tooltip } from '@devexpress/dx-react-chart-material-ui';
import { ValueScale, EventTracker } from '@devexpress/dx-react-chart';

import { setIsOnboardingOpen } from '../../../components/Onboarding/store/actions';
import { loadTotalOccupation, loadPaidOut, loadCanceled, loadAltered, loadCashflow, loadGraphics } from '../store/thunk';
import { loadFilters } from '../../../components/Filter/store/thunk';

import { loadNewPasswordTerms, loadUserNewPasswordTerms, resetUserPassword } from 'pages/Profile/store/thunk';
import { setIsConsultNewPasswordTermsRequestDone, setIsConsultUserNewPasswordTermsRequestDone } from 'pages/Profile/store/actions';

import {
  formatCurrency,
  convertGraphicData,
  convertTypeToTimeRange,
  convertToPercentage,
  convertTypeToNumberOfDays,
  convertNumberToTime
} from '../../../helpers/converters';

import { formatDaysBetweenDates } from '../../../helpers/formatters';
import useWindowDimensions from '../../../helpers/hooks/useWindowDimensions';

import { onboardingSteps } from 'components/Onboarding/components/resources';

import { useStyles } from './styles';

export default function Dashboard() {
  const dispatch = useDispatch();

  const classes = useStyles();
  const { width } = useWindowDimensions();
  const isMd = width < 1280;

  const isProduction = process.env.REACT_APP_IS_JUMP_PRODUCTION == 'true';

  const { userId, isConsultNewPasswordTermsRequestDone, isConsultUserNewPasswordTermsRequestDone, userOwnTerms, userTermsNotAccepted } = useSelector(state => state.profile);
  const { graphics, cashFlow, serviceOrders, occupation, isLoading } = useSelector(state => state.dashboard);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const type = useSelector(state => state.filters.graphicTimeRange);
  const graphicTimeRange = useSelector(state => state.dateFilter);
  const { isFromRegistration, isOnboardingOpen } = useSelector(state => state.onboarding);

  const { paidOut, canceled, altered } = serviceOrders;

  const [isPasswordUpdateOpen, setIsPasswordUpdateOpen] = useState(false);
  const [isPasswordUsersUpdateOpen, setIsPasswordUsersUpdateOpen] = useState(false);
  const [isPasswordUpdateLoading, setIsPasswordUpdateLoading] = useState(false);
  const [isPasswordUpdateSuccessOpen, setIsPasswordUpdateSuccessOpen] = useState(false);

  const [isTotalOccupationLoading, setIsTotalOccupationLoading] = useState(false);
  const [isPaidOutLoading, setIsPaidOutLoading] = useState(false);
  const [isCanceledLoading, setIsCanceledLoading] = useState(false);
  const [isAlteredLoading, setIsAlteredLoading] = useState(false);
  const [isGraphicsLoading, setIsGraphicsLoading] = useState(false);
  const [isCashflowLoading, setIsCashflowLoading] = useState(false);

  const startDate = moment().subtract(convertTypeToNumberOfDays(type), 'days');
  const endDate = moment().add(1, 'days');
  const setDayHours = map(convertNumberToTime, range(0, 24));

  const [customGraphicDatesArray, setCustomGraphicDatesArray] = useState([]);

  const graphicTimeRangeStartDate = moment(graphicTimeRange?.startDate).format('DD/MM/YYYY');
  const graphicTimeRangeEndDate = moment(graphicTimeRange?.endDate).format('DD/MM/YYYY');
  const isCustomTimeRangeSingleDay = moment(graphicTimeRangeEndDate, 'DD/MM/YYYY').diff(moment(graphicTimeRangeStartDate, 'DD/MM/YYYY'), 'days') == 0;

  const setDateTimeRanges =
    type == 'day' ||
    type == 'yesterday' ||
    (type == 'custom' && isCustomTimeRangeSingleDay)
      ? setDayHours
      : formatDaysBetweenDates(startDate, endDate);

  const { balance, entry, payments } = graphics;

  const setGraphicData = (i, cashFlowType) => {
    const graphicType = type == 'custom' && isCustomTimeRangeSingleDay ? 'day' : type;
    return convertGraphicData(cashFlowType, graphicType, setDateTimeRanges);
  }

  const mainChart = setDateTimeRanges.map((range, i) => {
    return {
      day: range,
      entry: setGraphicData(0, entry)[i],
      payments: setGraphicData(1, payments)[i],
      balance: setGraphicData(2, balance)[i]
    }
  });

  const formatCustomGraphicData = (entry, payments, balance) => {
    const customGraphicDates = balance.map(range => {
      return moment(range.days).format('DD/MM/YYYY');
    });

    const paymentsOffsetDays =
      difference(
        customGraphicDates,
        payments?.map(payment => moment(payment?.days).format('DD/MM/YYYY'))
      )
      .map(days => {
        return {
          days: moment(days, 'DD/MM/YYYY').format('YYYYMMDD'),
          amount: 0
        }
      });

    const paymentsToBalanceDays = [...paymentsOffsetDays, ...payments]
      ?.sort((a, b) => moment(a.days) - moment(b.days));

    const formattedCustomGraphicDates = customGraphicDates?.map((date, i) => {
      return {
        day: date,
        entry: parseFloat(entry[i]?.amount) || 0,
        balance: parseFloat(balance[i]?.amount) || 0,
        payments: moment(paymentsToBalanceDays[i]?.days).format('DD/MM/YYYY') == date
          ? parseFloat(paymentsToBalanceDays[i]?.amount)
          : 0
      }
    });

    let toDateGraphic = [];

    const startDate = moment(graphicTimeRange?.startDate).format('DD/MM/YYYY');
    const endDate = moment(graphicTimeRange?.endDate).format('DD/MM/YYYY');
    const lastGraphicDay = formattedCustomGraphicDates[formattedCustomGraphicDates.length - 1]?.day;

    if(lastGraphicDay != endDate) {
      let daysDifferenceToDate = moment(endDate, 'DD/MM/YYYY').diff(moment(lastGraphicDay, 'DD/MM/YYYY'), 'days') || 0;

      if(+daysDifferenceToDate == 0) {
        daysDifferenceToDate = moment(endDate, 'DD/MM/YYYY').diff(moment(startDate, 'DD/MM/YYYY'), 'days') + 1;
      }

      if(+daysDifferenceToDate < 0) {
        daysDifferenceToDate = 0;
      }

      toDateGraphic = Array(daysDifferenceToDate)
        .fill({})
        .map((_, index) => {
          return {
            day: moment(endDate, 'DD/MM/YYYY').subtract((daysDifferenceToDate - (index + 1)), 'days').format('DD/MM/YYYY'),
            entry: 0,
            payments: 0,
            balance: 0
          }
        });
    }

    const graphics = [...formattedCustomGraphicDates, ...toDateGraphic]
      .map(graphic => {
        return {
          ...graphic,
          day: moment(graphic?.day, 'DD/MM/YYYY').format('DD/MM')
        }
      });

    setCustomGraphicDatesArray(graphics);
  }

  useEffect(() => {
    if(type == 'custom' && !isCustomTimeRangeSingleDay) {
      formatCustomGraphicData(entry, payments, balance);
    }
  }, [type, graphics]);

  useEffect(() => {
    dispatch(loadFilters(userId, establishmentId));
  }, []);

  useEffect(() => {
    if(!isConsultNewPasswordTermsRequestDone) {
      dispatch(loadNewPasswordTerms(userId)).then(data => {
        if(data?.length == 0) {
          setIsPasswordUpdateOpen(true);
        }
      });
    }
  }, [isConsultNewPasswordTermsRequestDone]);

  useEffect(() => {
    dispatch(loadUserNewPasswordTerms(userId)).then(() => {
      dispatch(setIsConsultUserNewPasswordTermsRequestDone());
    });
  }, []);

  useEffect(() => {
    if(!isConsultUserNewPasswordTermsRequestDone) {
      if(userOwnTerms?.length > 0 && userTermsNotAccepted?.length > 0) {
        setIsPasswordUsersUpdateOpen(true);
      }
    }
  }, [isConsultUserNewPasswordTermsRequestDone, userOwnTerms, userTermsNotAccepted]);

  useEffect(() => {
    if(isFromRegistration) {
      dispatch(setIsOnboardingOpen(true));
    }
  }, [isFromRegistration]);

  useEffect(() => {
    if(type) {
      const { endDate, startDate } = graphicTimeRange;

      const params = {
        userId,
        establishmentId,
        type,
        startDate,
        endDate
      }

      setIsTotalOccupationLoading(true);
      setIsPaidOutLoading(true);
      setIsCanceledLoading(true);
      setIsAlteredLoading(true);
      setIsCashflowLoading(true);
      setIsGraphicsLoading(true);

      dispatch(loadTotalOccupation(params)).then(() => {
        setIsTotalOccupationLoading(false);

        dispatch(loadPaidOut(params)).then(() => {
          setIsPaidOutLoading(false);

          dispatch(loadCanceled(params)).then(() => {
            setIsCanceledLoading(false);

            dispatch(loadAltered(params)).then(() => {
              setIsAlteredLoading(false);

              dispatch(loadCashflow(params)).then(() => {
                setIsCashflowLoading(false);

                dispatch(loadGraphics(params)).then(() => {
                  setIsGraphicsLoading(false);
                });
              });
            });
          });
        });
      });
    }
  }, [type, graphicTimeRange, establishmentId, userId]);

  return(
    <Loader isLoading={isLoading}>
      {(isProduction && isFromRegistration && isOnboardingOpen) && (
        <Onboarding
          step={onboardingSteps.DASHBOARD}
          buttonTitle='Finalizar'
        />
      )}
      {isPasswordUpdateOpen && (
        <PasswordUpdate
          isLoading={isPasswordUpdateLoading}
          isOpen={isPasswordUpdateOpen}
          setIsOpen={setIsPasswordUpdateOpen}
          onSuccess={values => {
            setIsPasswordUpdateLoading(true);

            dispatch(resetUserPassword(userId, values))
              .then(() => {
                dispatch(setIsConsultNewPasswordTermsRequestDone());
                setIsPasswordUpdateOpen(false);
                setIsPasswordUpdateSuccessOpen(true);
              })
              .finally(() => setIsPasswordUpdateLoading(false));
          }}
        />
      )}
      {isPasswordUpdateSuccessOpen && (
        <PasswordUpdateSuccess
          isOpen={isPasswordUpdateSuccessOpen}
        />
      )}
      {isPasswordUsersUpdateOpen && (
        <PasswordUsersUpdate
          isOpen={isPasswordUsersUpdateOpen}
          setIsOpen={setIsPasswordUsersUpdateOpen}
          users={userTermsNotAccepted}
        />
      )}
      <Grid container spacing={2} className={classes.grid}>
        <Grid item xs={6} md={3}>
          <Paper className={classes.container} style={{ borderBottom: '5px solid #13BC4B' }}>
            <Loader isLoading={isTotalOccupationLoading}>
              <Typography variant="subtitle1" color="primary" align="center">
                Ocupação atual
              </Typography>
              <Typography variant="h4" color="primary" align="center">
                {occupation?.total}
              </Typography>
            </Loader>
          </Paper>
          {!isTotalOccupationLoading && (
            <Typography variant="body2" align="center" className={classes.footer}>
              Ocupação total: {occupation?.vacancies}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.container} style={{ borderBottom: '5px solid #13BC4B' }}>
            <Loader isLoading={isPaidOutLoading}>
              <Typography variant="subtitle1" color="primary" align="center">
                OS Pagas
              </Typography>
              <Typography variant="h4" color="primary" align="center">
                {paidOut?.total}
              </Typography>
            </Loader>
          </Paper>
          {!isPaidOutLoading && (
            <Typography variant="body2" align="center" className={classes.footer}>
              Total: {formatCurrency(paidOut?.totalAmount || 0)}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.container} style={{ borderBottom: '5px solid #F66C6B' }}>
            <Loader isLoading={isCanceledLoading}>
              <Typography variant="subtitle1" color="primary" align="center">
                OS Canceladas
              </Typography>
              <Typography variant="h4" color="primary" align="center">
                {canceled?.total}
              </Typography>
            </Loader>
          </Paper>
          {!isCanceledLoading && (
            <Typography variant="body2" align="center" className={classes.footer}>
              Total: {formatCurrency(canceled?.totalAmount || 0)}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper className={classes.container} style={{ borderBottom: '5px solid #F5B941' }}>
            <Loader isLoading={isAlteredLoading}>
              <Typography variant="subtitle1" color="primary" align="center">
                OS Alteradas
              </Typography>
              <Typography variant="h4" color="primary" align="center">
                {altered?.total}
              </Typography>
            </Loader>
          </Paper>
          {!isAlteredLoading && (
            <Typography variant="body2" align="center" className={classes.footer}>
              {convertToPercentage(altered?.total, paidOut?.total)} do total de OSs
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <Paper className={isMd ? classes.cashFlowContainer : classes.cashFlowMdContainer}>
            <Grid className={isCashflowLoading ? classes.flexCenter : null}>
              <Loader isLoading={isCashflowLoading}>
                <Grid container spacing={2} style={{ padding: '5px 0px 20px 0px' }}>
                  <Grid item xs={6} md={4} align="center">
                    <Typography color="primary">
                      Receita
                    </Typography>
                    <Typography variant="h6" color="textSecondary">
                      {formatCurrency(cashFlow?.entry || 0)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      color="primary"
                      value={100}
                      className={classes.progress}
                    />
                  </Grid>
                  <Grid item xs={6} md={4} align="center">
                    <Typography color="primary">
                      Despesa
                    </Typography>
                    <Typography variant="h6" color="textSecondary">
                      {formatCurrency(cashFlow?.exit || 0)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      color="error"
                      value={100}
                      className={[classes.progress, classes.error]}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} align="center">
                    <Typography color="primary">
                      Lucro
                    </Typography>
                    <Typography variant="h6" color="textSecondary">
                      {formatCurrency(cashFlow?.balance || 0)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      color="success"
                      value={100}
                      className={[classes.progress, classes.success]}
                    />
                  </Grid>
                </Grid>
              </Loader>
            </Grid>
            <Grid className={isGraphicsLoading ? classes.flexCenter : null}>
              <Loader isLoading={isGraphicsLoading}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body1" color="primary">
                      Fluxo de caixa
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                      {convertTypeToTimeRange(type)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Chart
                    height={300}
                    style={{ paddingBottom: '20px' }}
                    data={(type == 'custom' && !isCustomTimeRangeSingleDay) ? customGraphicDatesArray : mainChart}
                  >
                    <ValueScale name="balance" />
                    <ValueScale name="day" />
                    <ArgumentAxis showLabels={width > 768} />
                    <ValueAxis
                      scaleName="day"
                      position="right"
                      showGrid={false}
                      showLine
                      showTicks
                    />
                    <SplineSeries
                      scaleName="day"
                      name="Receitas"
                      valueField="entry"
                      argumentField="day"
                      color="#1E5168"
                    />
                    <SplineSeries
                      scaleName="day"
                      name="Despesas"
                      valueField="payments"
                      argumentField="day"
                      color={red[600]}
                    />
                    <SplineSeries
                      scaleName="day"
                      name="Lucro"
                      valueField="balance"
                      argumentField="day"
                      color={green[600]}
                    />
                    <EventTracker />
                    <Tooltip
                      enterTouchDelay={0}
                      contentComponent={tooltip => (
                        <>
                          <Typography
                            variant="body1"
                            color="primary"
                            className={classes.bold}
                          >
                            {tooltip.targetItem.series}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {formatCurrency(tooltip.text || 0)}
                          </Typography>
                        </>
                      )}
                    />
                  </Chart>
                </Grid>
              </Loader>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Loader>
  );
}